import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { AvatarInitial, Button } from '@lesmills-international/components';
import COLORS from '../../../themes/colors';
import { CHARACTER_LIMITS } from '../../../constants/workout';
import { getMultilineHelperText } from '../../../utils/workoutCommentsHelpers';

// 🎯 OPTIMIZATION: Custom debounce hook for character counter
const useDebounce = (value: number, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
`;

const CommentInput = styled.input.withConfig({
  shouldForwardProp: (prop) => prop !== 'showCharacterCounter',
})<{ showCharacterCounter?: boolean }>`
  flex: 1;
  margin-left: 24px;
  padding: ${(props) =>
    props.showCharacterCounter
      ? '12px 80px 40px 16px' // Much larger bottom padding (40px) for character counter + typing space
      : '12px 16px'}; // Standard padding for no counter
  border-radius: 4px;
  font-size: 16px;
  background-color: ${COLORS.darkGrey};
  color: ${COLORS.white};
  border: 0;
  min-height: ${(props) =>
    props.showCharacterCounter ? '48px' : 'auto'}; // Taller for new API (Twitter-style)
  
  /* Prevent UI breaking with long words during typing - RESPONSIVE */
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  
  /* Responsive font size for all devices */
  font-size: clamp(14px, 4vw, 16px);
  
  /* Device-specific word breaking */
  @media (max-width: 480px) {
    /* Mobile: More aggressive breaking */
    word-break: break-all;
    overflow-wrap: anywhere;
  }
  
  @media (min-width: 1025px) {
    /* Large screens & TVs: Ensure readability */
    font-size: 16px;
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
  &::placeholder {
    color: ${COLORS.lightGreyAE};
    font-family: Inter Light;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
`;

// Textarea for multiline support (NEW API)
const CommentTextarea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => prop !== 'showCharacterCounter',
})<{ showCharacterCounter?: boolean }>`
  flex: 1;
  margin-left: 24px;
  padding: ${(props) =>
    props.showCharacterCounter
      ? '16px 80px 48px 16px' // Much larger bottom padding (48px) for character counter + typing space
      : '16px 16px 20px 16px'}; // Standard padding for no counter
  border-radius: 4px;
  font-size: 16px;
  background-color: ${COLORS.darkGrey};
  color: ${COLORS.white};
  border: 0;
  min-height: ${(props) =>
    props.showCharacterCounter ? '48px' : 'auto'}; // Taller for new API (Twitter-style)
  max-height: 120px; // Limit height for multiline
  resize: vertical; // Allow vertical resizing only
  font-family: Inter Light; // Match input font
  line-height: clamp(20px, 3vw, 24px);
  
  /* Ensure proper scrolling with bottom space for character counter */
  scroll-padding-bottom: 30px; // Keep space when scrolling to bottom
  scrollbar-width: thin; // Thin scrollbar for better UX
  
  /* Create visual buffer at bottom to prevent text hiding behind counter */
  &::after {
    content: '';
    display: block;
    height: 24px; // Extra space buffer at bottom
    width: 100%;
    pointer-events: none;
  }
  
  /* Improve scrolling behavior */
  overflow-y: auto;
  scroll-behavior: smooth;
  
  /* Prevent UI breaking with long words during typing - RESPONSIVE */
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  
  /* Responsive font size for all devices */
  font-size: clamp(14px, 4vw, 16px);
  
  /* Device-specific word breaking and sizing */
  @media (max-width: 480px) {
    /* Mobile: More aggressive breaking + responsive padding */
    word-break: break-all;
    overflow-wrap: anywhere;
    max-height: clamp(80px, 25vw, 120px);
    padding: ${(props) =>
      props.showCharacterCounter
        ? 'clamp(12px, 4vw, 16px) clamp(60px, 20vw, 80px) clamp(40px, 10vw, 48px) clamp(12px, 4vw, 16px)' // Much more bottom space on mobile
        : 'clamp(12px, 4vw, 16px)'};
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    /* Tablet: Moderate sizing */
    max-height: clamp(100px, 20vw, 120px);
    font-size: clamp(15px, 3vw, 16px);
  }
  
  @media (min-width: 1025px) {
    /* Large screens & TVs: Fixed optimal sizing */
    font-size: 16px;
    line-height: 24px;
    max-height: 120px;
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
  &::placeholder {
    color: ${COLORS.lightGreyAE};
    font-family: Inter Light;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
`;

const ButtonsContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'hasHelperText',
})<{ hasHelperText?: boolean }>`
  display: flex;
  flex-direction: ${(props) =>
    props.hasHelperText ? 'column' : 'row'}; // Column when helper text present
  margin-top: 24px;
  margin-left: 64px;
  gap: ${(props) => (props.hasHelperText ? '8px' : '24px')}; // Smaller gap for vertical layout
`;

export interface PostCommentProps {
  onPost: (comment: string, parentId?: string) => void;
  onCancel: () => void;
  author: string;
  cancelText: string;
  postCommentText: string;
  inputPlaceholderText: string;
  validateSpaces?: boolean; // New prop for enhanced validation (backwards compatible)
  showCharacterCounter?: boolean; // New prop for character counter (backwards compatible)
  maxCharacters?: number; // Maximum character limit (backwards compatible)
  allowNewLines?: boolean; // New prop for multiline support (backwards compatible)
  prismicData?: any; // New prop for Prismic text support (backwards compatible)
}

// 🎯 OPTIMIZATION: Memoized Character Counter Component (Twitter-style, inside input field) - Gradual color progression
const CharacterCounter = React.memo(({ current, max }: { current: number; max: number }) => {
  const percentage = current / max;
  
  // Gradual color progression - red only at actual limit
  const isAtLimit = current >= max; // Red only at 280 chars
  const isFinalWarning = percentage >= CHARACTER_LIMITS.FINAL_WARNING; // 95% (266 chars) - Dark orange (longer duration)
  const isSecondWarning = percentage >= CHARACTER_LIMITS.SECOND_WARNING; // 85% (238 chars) - Medium grey  
  const isFirstWarning = percentage >= CHARACTER_LIMITS.FIRST_WARNING; // 75% (210 chars) - Light grey

  // Gradual color progression function using design system colors
  const getCounterColor = () => {
    if (isAtLimit) return COLORS.errorRed; // Red only at 280 chars
    if (isFinalWarning) return COLORS.errorText; // Dark orange-red at 266+ chars (longer duration)
    if (isSecondWarning) return COLORS.lightGreyB0; // Light grey at 238+ chars  
    if (isFirstWarning) return COLORS.lightGreyAE; // Lighter grey at 210+ chars
    return COLORS.lightGrey7B; // Default grey for normal typing
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 'clamp(12px, 3vw, 16px)', // Positioned higher to create clear separation from text
        right: 'clamp(8px, 2vw, 12px)', // Consistent right spacing
        fontSize: 'clamp(10px, 2vw, 12px)', // Slightly smaller to prevent overlap
        fontWeight: isAtLimit ? '600' : '500', // Bolder only when at actual limit
        color: getCounterColor(),
        backgroundColor: COLORS.darkGrey, // Match input background for seamless look
        padding: 'clamp(2px, 0.5vw, 3px) clamp(4px, 1vw, 6px)', // Compact padding
        borderRadius: 'clamp(2px, 0.5vw, 3px)', // Small border radius
        border: `1px solid ${COLORS.whiteOpacity01}`, // Subtle border for definition
        pointerEvents: 'none', // Don't interfere with input
        zIndex: 2,
        whiteSpace: 'nowrap', // Prevent text wrapping
        fontFamily: 'Inter Light', // Match input font
      }}
    >
      {current}/{max}
    </div>
  );
});

// 🎯 OPTIMIZATION: Memoized PostComment component for better performance
const PostComment: React.FC<PostCommentProps> = React.memo(({
  onPost,
  onCancel,
  author,
  cancelText,
  postCommentText,
  inputPlaceholderText,
  validateSpaces = false, // Default to false for backwards compatibility
  showCharacterCounter = false, // Default to false for backwards compatibility
  maxCharacters = CHARACTER_LIMITS.MAX_LENGTH, // Default backend limit
  allowNewLines = false, // Default to false for backwards compatibility
  prismicData, // Prismic data for i18n support
}) => {
  const [comment, setComment] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  // 🎯 OPTIMIZATION: Debounced character counter to prevent excessive re-renders
  const debouncedCharCount = useDebounce(comment.length, 50); // 50ms debounce for smooth typing
  
  // 🎯 OPTIMIZATION: Focus management for better UX
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  
  // Reset focus and form after successful post
  useEffect(() => {
    if (!comment && inputRef.current) {
      inputRef.current.blur(); // Remove focus after posting
    }
  }, [comment]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const handlePost = () => {
    // 🔧 FIX: Prevent any action if button is disabled
    if (!isValidComment) {
      return; // Do nothing if invalid - don't hide buttons or trigger any action
    }
    
    onPost(comment);
    setComment('');
    setIsInputFocused(false); // Hide the comment section after posting
  };

  const handleCancel = () => {
    // 🔧 FIX: Cancel should always work, no validation needed
    setComment('');
    setIsInputFocused(false); // Hide the comment section after canceling
    onCancel();
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Check if the blur is caused by clicking on one of our buttons
    // If so, don't hide the buttons immediately
    const relatedTarget = e.relatedTarget as HTMLElement;
    const buttonsContainer = document.querySelector('[data-post-comment-buttons]');

    // 🔧 FIX: Enhanced button detection - check for buttons container or any button element
    const isClickingOnButton = (relatedTarget && buttonsContainer && buttonsContainer.contains(relatedTarget)) ||
                              (relatedTarget && relatedTarget.tagName === 'BUTTON') ||
                              (relatedTarget && relatedTarget.closest('button')) ||
                              (relatedTarget && relatedTarget.closest('[data-post-comment-buttons]'));

    if (isClickingOnButton) {
      // Don't hide buttons if clicking on them (including disabled ones)
      return;
    }

    // 🔧 FIX: Don't hide buttons if there's content and the comment is invalid
    // This prevents buttons disappearing when clicking disabled button
    if (comment.trim() && !isValidComment) {
      // Keep buttons visible when user has content but button is disabled
      return;
    }

    // Delay setting isInputFocused to false to allow for touchpad interactions
    setTimeout(() => {
      setIsInputFocused(false);
    }, 200);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      if (allowNewLines && event.shiftKey) {
        // NEW API: Shift+Enter posts comment
        event.preventDefault();
        handlePost();
      } else if (!allowNewLines) {
        // LEGACY API: Enter posts comment (original behavior)
        event.preventDefault();
        handlePost();
      }
      // NEW API: Plain Enter allows new line (no action needed)
    }
  };

  const isOverLimit = comment.length > maxCharacters; // Disabled only when actually over limit
  const isValidComment = validateSpaces ? comment.trim() && !isOverLimit : comment && !isOverLimit;

  return (
    <Container>
      <InputContainer style={{ position: 'relative' }}>
        <AvatarInitial name={author} />
        {allowNewLines ? (
          // NEW API: Textarea for multiline support
          <CommentTextarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            placeholder={inputPlaceholderText}
            value={comment}
            onChange={handleChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyPress}
            showCharacterCounter={showCharacterCounter}
          />
        ) : (
          // LEGACY API: Original input (single line)
          <CommentInput
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            placeholder={inputPlaceholderText}
            value={comment}
            onChange={handleChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyPress}
            showCharacterCounter={showCharacterCounter}
          />
        )}
        {/* 🎯 OPTIMIZATION: Lazy loaded + debounced character counter - only shown for new API */}
        {showCharacterCounter && <CharacterCounter current={debouncedCharCount} max={maxCharacters} />}
      </InputContainer>
      {isInputFocused && (
        <ButtonsContainer hasHelperText={allowNewLines}>
          {/* Multiline helper text - ABOVE buttons, left-aligned, only for new API */}
          {allowNewLines && (
            <div
              style={{
                fontSize: 'clamp(10px, 2vw, 11px)',
                color: COLORS.lightGrey7B,
                fontStyle: 'italic',
                textAlign: 'left', // Left-aligned as requested
                marginBottom: 'clamp(4px, 1vw, 8px)', // Space before buttons
              }}
            >
              {getMultilineHelperText(prismicData)}
            </div>
          )}
          {/* Buttons in horizontal layout BELOW helper text */}
          <div 
            data-post-comment-buttons // 🔧 FIX: Add data attribute for blur handler detection
            style={{ display: 'flex', gap: '24px' }}
          >
            <Button 
              style={{ 
                borderRadius: '4px',
                // 🔧 FIX: Consistent button styling - cancel always enabled
                cursor: 'pointer',
                opacity: 1,
              }} 
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              disabled={!isValidComment}
              inverted
              style={{ 
                borderRadius: '4px',
                // 🔧 FIX: Proper disabled button styling
                cursor: !isValidComment ? 'not-allowed' : 'pointer',
                opacity: !isValidComment ? 0.5 : 1,
                pointerEvents: !isValidComment ? 'none' : 'auto', // Prevent all mouse interactions when disabled
              }}
              onClick={handlePost}
              onMouseDown={(e) => {
                // 🔧 FIX: Prevent any mouse events on disabled button
                if (!isValidComment) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {postCommentText}
            </Button>
          </div>
        </ButtonsContainer>
      )}
    </Container>
  );
});

export default PostComment;
